import { DataGrid, GridSortModel } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getCommonDataGridProps,
  ICommonDataGridProps,
  ITEMS_PER_PAGE,
  mapColumnsToCommon,
  mapRowsToCommon,
  WithId,
} from "./common";
import {
  Box,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
} from "@mui/material";

interface IProps<T extends WithId>
  extends Omit<ICommonDataGridProps<T>, "onPageSizeChange" | "onPageChange"> {
  totalCount: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (sortModel: GridSortModel) => void;
  isLoading?: boolean;
  isRowDisabled?: (row: T) => boolean;
}

export const LazyDataGrid = <T extends WithId>({
  id,
  rows,
  columns,
  totalCount,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  sortModel,
  onSortModelChange,
  onSelectedRowsChange,
  isLoading,
  rowHeight,
  getRowHeight,
  onRowClick,
  disabledRowIds,
  rowSelectionModel,
  isRowDisabled,
  pageSizeInitial,
}: IProps<T>): React.ReactElement<IProps<T>> => {
  const [innerPage, setInnerPage] = useState(0);
  const [innerPageSize, setInnerPageSize] = useState(
    pageSizeInitial ?? ITEMS_PER_PAGE
  );
  const commonProps = getCommonDataGridProps(
    onRowClick,
    disabledRowIds,
    isRowDisabled
  );

  const getSavedColumnWidths = () => {
    if (id) {
      const saved = localStorage.getItem(`table-${id}`);

      const parsedSaved = saved ? JSON.parse(saved) : {};

      if (typeof parsedSaved === "object" && !Array.isArray(parsedSaved)) {
        return parsedSaved;
      }
    }

    return {};
  };

  const savedWidths = useRef(getSavedColumnWidths());

  return (
    <Stack>
      <DataGrid<T>
        {...commonProps}
        sortingMode="server"
        paginationMode="server"
        disableRowSelectionOnClick
        rows={mapRowsToCommon(rows)}
        columns={mapColumnsToCommon(
          columns.map((col) => ({
            ...col,
            width: savedWidths.current?.[col.field] || col.width,
          }))
        )}
        rowHeight={rowHeight}
        getRowHeight={getRowHeight}
        loading={isLoading}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        onRowSelectionModelChange={onSelectedRowsChange}
        rowSelectionModel={rowSelectionModel}
        keepNonExistentRowsSelected={Boolean(onSelectedRowsChange)}
        rowSelection={Boolean(onSelectedRowsChange)}
        checkboxSelection={Boolean(onSelectedRowsChange)}
        rowCount={totalCount}
        paginationModel={{ page: innerPage, pageSize: innerPageSize }}
        hideFooterPagination={!onPageChange}
        onPaginationModelChange={({ page, pageSize }) => {
          if (page !== innerPage) {
            setInnerPage(page);
            onPageChange?.(page);
          }

          if (pageSize !== innerPageSize) {
            setInnerPageSize(pageSize);
            onPageSizeChange?.(pageSize);
          }
        }}
        hideFooter
        pageSizeOptions={pageSizeOptions ?? [5, 10, 20, 40]}
        onColumnResize={(params) => {
          if (id) {
            const currentWidths = getSavedColumnWidths();
            const updatedWidths = {
              ...currentWidths,
              [params.colDef.field]: params.width,
            };
            savedWidths.current = updatedWidths;
            localStorage.setItem(`table-${id}`, JSON.stringify(updatedWidths));
          }
        }}
      />
      <Stack
        sx={{
          color: "#fff",
          background: "#567287",
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          py: 1.5,
          px: 1.5,
          "& *": {
            color: "#fff",
          },
        }}
        direction="row"
        alignItems="center"
        justifyContent="end"
      >
        <Stack direction="row" alignItems="center" mr={2}>
          <Box fontSize={13} mr={2}>
            Počet riadkov na stránku
          </Box>
          <TextField
            select
            variant="filled"
            value={innerPageSize}
            onChange={(e) => {
              setInnerPageSize(parseInt(e.target.value, 10));
              onPageSizeChange(parseInt(e.target.value, 10));
            }}
            sx={{
              width: 70,
              borderBottom: "0px",
              color: "#fff",
              py: 1,
              "& *": { color: "white !important" },
              ".MuiSelect-select": { py: 0.5 },
            }}
          >
            {(pageSizeOptions ?? [5, 10, 20, 40]).map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Pagination
          count={Math.ceil(totalCount / innerPageSize)}
          page={innerPage + 1}
          onChange={(_, page) => {
            setInnerPage(page - 1);
            onPageChange?.(page - 1);
          }}
          showFirstButton
          showLastButton
          sx={{
            display: "flex",
            justifyContent: "center",
            "& *": { color: "white !important" },
          }}
        />
      </Stack>
    </Stack>
  );
};
